(() => {
  /**
   * Initialize overlay visibility based on details[open] state
   */
  const initOverlay = (detailsElement) => {
    const overlay = detailsElement.querySelector('[data-mega-menu-overlay]');
    if (!overlay) {
      return;
    }

    const updateOverlay = () => {
      if (detailsElement.open) {
        overlay.setAttribute('aria-hidden', 'false');
      } else {
        overlay.setAttribute('aria-hidden', 'true');
      }
    };

    // Initial state
    updateOverlay();

    // Watch for open/close changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          updateOverlay();
        }
      });
    });

    observer.observe(detailsElement, {
      attributes: true,
      attributeFilter: ['open'],
    });

    // Also listen to toggle events as fallback
    detailsElement.addEventListener('toggle', updateOverlay);

    // Close menu when hovering overlay (desktop only)
    // This fixes the issue where hovering the overlay doesn't close the menu
    // because the overlay is inside <details>, so pointer never leaves the disclosure element
    const isDesktop = () => window.matchMedia('(min-width: 1150px)').matches;
    
    const handleOverlayHover = (e) => {
      // Only handle on desktop
      if (!isDesktop()) {
        return;
      }

      // Find the parent <details is="mega-menu-disclosure"> element
      const details = overlay.closest('details[is="mega-menu-disclosure"]');
      if (!details || !details.open) {
        return;
      }

      // Close the menu by removing the open attribute
      // This will trigger the toggle event and update aria-hidden
      details.open = false;
    };

    // Use pointerenter for better cross-device support (mouse + trackpad)
    // Fallback to mouseenter for older browsers
    overlay.addEventListener('pointerenter', handleOverlayHover);
    overlay.addEventListener('mouseenter', handleOverlayHover);
  };

  const initMenu = (menu) => {
    if (!menu || menu.dataset.cmmdInit === 'true') {
      return;
    }

    menu.dataset.cmmdInit = 'true';

    const navLinks = Array.from(
      menu.querySelectorAll('.c-mega-menu-desktop__nav-link[data-nav-index]')
    );
    const groups = Array.from(
      menu.querySelectorAll('.c-mega-menu-desktop__products-group[data-nav-index]')
    );

    if (!navLinks.length || !groups.length) {
      return;
    }

    const setActive = (index) => {
      groups.forEach((group) => {
        group.classList.toggle(
          'c-mega-menu-desktop__products-group--active',
          group.dataset.navIndex === index
        );
      });

      navLinks.forEach((link) => {
        link.classList.toggle(
          'c-mega-menu-desktop__nav-link--active',
          link.dataset.navIndex === index
        );
      });
    };

    const defaultIndex = navLinks[0].dataset.navIndex || '0';
    setActive(defaultIndex);

      navLinks.forEach((link) => {
        const index = link.dataset.navIndex;

        link.addEventListener('mouseenter', () => setActive(index));
        link.addEventListener('focus', () => setActive(index));
      });
  };

  /**
   * Prevent navigation on mega menu summary clicks
   * Mega menu items should only open/close the menu, not navigate to URLs
   */
  const preventSummaryNavigation = (detailsElement) => {
    const summary = detailsElement.querySelector('summary');
    if (!summary) {
      return;
    }

    // Remove data-url if it exists (defensive cleanup)
    if (summary.hasAttribute('data-url')) {
      summary.removeAttribute('data-url');
    }

    // Prevent any click navigation attempts
    const handleClick = (e) => {
      // Only prevent if this is a mega menu disclosure
      if (detailsElement.getAttribute('is') === 'mega-menu-disclosure') {
        // Allow the default disclosure toggle behavior, but prevent navigation
        if (summary.hasAttribute('data-url')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    summary.addEventListener('click', handleClick, true);
  };

  const initAll = (root) => {
    const scope = root || document;
    
    // Initialize menus
    scope.querySelectorAll('.c-mega-menu-desktop').forEach(initMenu);
    
    // Initialize overlays for custom mega menus
    scope.querySelectorAll('details[is="mega-menu-disclosure"]:has(.c-mega-menu-desktop)').forEach((detailsElement) => {
      initOverlay(detailsElement);
      preventSummaryNavigation(detailsElement);
    });
    
    // Also handle non-custom mega menus (horizontal layout)
    scope.querySelectorAll('details[is="mega-menu-disclosure"]:not(:has(.c-mega-menu-desktop))').forEach(preventSummaryNavigation);
  };

  const attachListeners = () => {
    if (document.documentElement.dataset.cmmdListeners === 'true') {
      return;
    }

    document.documentElement.dataset.cmmdListeners = 'true';

    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAll(document);
      attachListeners();
    });
  } else {
    initAll(document);
    attachListeners();
  }
})();
